import { AfterViewInit, Directive, ElementRef, inject, OnDestroy, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { A11yRovingTabindexService } from '../services/a11y-roving-tabindex.service';

interface OwlInteractiveElementAttributes {
  ariaDisabled?: string;
  ariaSelected?: string;
  label: string;
  role: string;
  tabindex?: string;
}

@Directive({
  selector: '[khA11yOwlCarousel]'
})
export class A11yOwlCarouselDirective implements AfterViewInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly rovingTabindexService = inject(A11yRovingTabindexService);
  private readonly translationService = inject(TranslationService);
  private readonly enhancedInteractiveElements = new WeakSet<HTMLElement>();
  private readonly cleanupListeners: (() => void)[] = [];

  private mutationObserver: MutationObserver | null = null;
  private previousLabel = 'Previous';
  private nextLabel = 'Next';
  private slideLabel = 'Slide';

  public constructor() {
    this.translationService.translationsReady$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.previousLabel = this.translationService.instant('A11Y_CAROUSEL.PREVIOUS') || this.previousLabel;
        this.nextLabel = this.translationService.instant('A11Y_CAROUSEL.NEXT') || this.nextLabel;
        this.slideLabel = this.translationService.instant('A11Y_CAROUSEL.SLIDE') || this.slideLabel;
        this.enhanceCarouselDom();
      });
  }

  public ngAfterViewInit(): void {
    this.enhanceCarouselDom();
    this.mutationObserver = new MutationObserver(() => this.enhanceCarouselDom());
    this.mutationObserver.observe(this.elementRef.nativeElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class']
    });
  }

  public ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    this.cleanupListeners.forEach((cleanup) => cleanup());
  }

  private enhanceCarouselDom(): void {
    const host = this.elementRef.nativeElement;

    this.setAttributes(host.querySelector('.owl-stage-outer'), { 'aria-live': 'polite' });
    this.setAttributes(host.querySelector('.owl-stage'), { 'aria-live': 'polite' });
    this.enhanceNavButton(host.querySelector('.owl-prev'), this.previousLabel);
    this.enhanceNavButton(host.querySelector('.owl-next'), this.nextLabel);
    this.enhanceDots(host.querySelector('.owl-dots'));
  }

  private enhanceNavButton(element: Element | null, label: string): void {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    const isDisabled = element.classList.contains('disabled');

    this.enhanceInteractiveElement(element, {
      ariaDisabled: isDisabled ? 'true' : 'false',
      label,
      role: 'button',
      tabindex: isDisabled ? '-1' : '0'
    });
  }

  private enhanceDots(element: Element | null): void {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    this.setAttributes(element, { role: 'tablist' });

    const dots = Array.from(element.querySelectorAll('.owl-dot'))
      .filter((dot): dot is HTMLElement => dot instanceof HTMLElement);
    const activeIndex = Math.max(dots.findIndex((dot) => dot.classList.contains('active')), 0);

    dots.forEach((dot, index) => this.enhanceDot(dot, index));
    this.rovingTabindexService.update(dots, activeIndex);
  }

  private enhanceDot(element: HTMLElement, index: number): void {
    const isActive = element.classList.contains('active');

    this.enhanceInteractiveElement(element, {
      ariaSelected: isActive ? 'true' : 'false',
      label: `${this.slideLabel} ${(index + 1).toString()}`,
      role: 'tab'
    });
  }

  private enhanceInteractiveElement(element: HTMLElement, attributes: OwlInteractiveElementAttributes): void {
    this.setAttributes(element, {
      'role': attributes.role,
      'aria-label': attributes.label,
      'aria-disabled': attributes.ariaDisabled,
      'aria-selected': attributes.ariaSelected,
      'tabindex': attributes.tabindex
    });

    this.hideChildren(element);
    this.listenForKeyboardActivation(element);
  }

  private hideChildren(element: HTMLElement): void {
    Array.from(element.children).forEach((child) => this.setAttributes(child, { 'aria-hidden': 'true' }));
  }

  private listenForKeyboardActivation(element: HTMLElement): void {
    if (this.enhancedInteractiveElements.has(element)) {
      return;
    }

    this.enhancedInteractiveElements.add(element);
    this.cleanupListeners.push(this.renderer.listen(element, 'keydown', (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      element.click();
    }));
  }

  private setAttributes(element: Element | null, attributes: Record<string, string | undefined>): void {
    if (!element) {
      return;
    }

    Object.entries(attributes).forEach(([name, value]) => {
      if (value) {
        this.renderer.setAttribute(element, name, value);
      }
    });
  }
}
